import { useAppSelector } from 'flow/hooks/useRedux';
import { CallRecordKey } from 'flow/types/EntityKeys';
import {
    selectCallRecordIsPersonalRecording,
    selectCallRecordIsTranscribable,
    selectCallRecordRecordedCallKey,
} from 'flow/selectors/call-records-selectors';
import { useGetRecordedCallTranscription } from './useGetRecordedCallTranscription';
import { useTranscribeRecordedCallWithProgress } from './useTranscribeRecordedCallWithProgress';
import { TranscriptionState } from 'flow/types/RecordedCalls';

export const useLegacyTranscription = (
    callRecordKey: CallRecordKey | undefined,
    enabled: boolean = true,
) => {
    const recordedCallKey = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordRecordedCallKey(state, callRecordKey)
            : undefined,
    );
    const isPersonalRecording = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordIsPersonalRecording(state, callRecordKey)
            : undefined,
    );
    const isTranscribable = useAppSelector((state) =>
        callRecordKey
            ? selectCallRecordIsTranscribable(state, callRecordKey)
            : false,
    );

    const fetchEnabled = enabled && !!isTranscribable;

    const { data: existingTranscription, isFetching: isAttemptingToFetch } =
        useGetRecordedCallTranscription(
            recordedCallKey,
            isPersonalRecording,
            fetchEnabled,
            Infinity,
            false, // No retry — this is a speculative check; 404 means no transcription exists
        );

    const {
        data: generatedTranscription,
        transcribe,
        progressState,
    } = useTranscribeRecordedCallWithProgress(
        recordedCallKey,
        isPersonalRecording,
    );

    const transcript =
        generatedTranscription?.fullTranscript ||
        existingTranscription?.fullTranscript;
    const summary =
        generatedTranscription?.summary || existingTranscription?.summary;
    const isGenerating =
        progressState === TranscriptionState.Started ||
        (progressState === TranscriptionState.Completed &&
            !generatedTranscription);

    return {
        transcript,
        summary,
        isGenerating,
        isAttemptingToFetch,
        transcribe,
    };
};
